/**
 * Theme Registry - Note'n Theme System
 *
 * Defines all available themes with metadata for UI display.
 * Each theme includes preview colors for the settings page.
 *
 * @module core/config/themes
 */

import type { DesignStyle } from '~/core/store/useGlobalStore';

// ============================================================================
// TYPES
// ============================================================================

export interface ThemePreviewColors {
  /** Background color for preview */
  bg: string;
  /** Card/surface color */
  card: string;
  /** Primary accent color */
  accent: string;
  /** Text color */
  text: string;
  /** Secondary accent (optional) */
  secondary?: string;
}

export interface ThemeDefinition {
  /** Unique identifier (matches DesignStyle) */
  id: DesignStyle;
  /** Display name */
  name: string;
  /** Korean display name */
  nameKo: string;
  /** Short description */
  description: string;
  /** Korean description */
  descriptionKo: string;
  /** Preview colors for light mode */
  previewLight: ThemePreviewColors;
  /** Preview colors for dark mode */
  previewDark: ThemePreviewColors;
  /** Author/contributor */
  author: string;
  /** Whether this is a built-in theme */
  isBuiltIn: boolean;
  /** Tags for filtering */
  tags: string[];
}

// ============================================================================
// THEME REGISTRY
// ============================================================================

export const THEMES: ThemeDefinition[] = [
  // 1. 소프트 (기본)
  {
    id: 'soft',
    name: 'Soft',
    nameKo: '소프트',
    description: 'Clean, modern design with soft shadows',
    descriptionKo: '부드러운 그림자와 둥근 모서리의 모던한 스타일',
    previewLight: {
      bg: '#f8fafc',
      card: '#ffffff',
      accent: '#3b82f6',
      text: '#0f172a',
    },
    previewDark: {
      bg: '#020617',
      card: '#0f172a',
      accent: '#3b82f6',
      text: '#f8fafc',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['minimal', 'modern', 'default'],
  },
  // 2. 모노크롬
  {
    id: 'mono',
    name: 'Mono',
    nameKo: '모노크롬',
    description: 'Pure black and white minimalism',
    descriptionKo: '순수한 흑백 미니멀리즘, 컬러 없이 명암만으로',
    previewLight: {
      bg: '#ffffff',
      card: '#ffffff',
      accent: '#404040',
      text: '#000000',
    },
    previewDark: {
      bg: '#0a0a0a',
      card: '#141414',
      accent: '#d4d4d4',
      text: '#ffffff',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['minimal', 'black', 'white', 'monochrome'],
  },
  // 3. 제주 감귤
  {
    id: 'jeju',
    name: 'Jeju',
    nameKo: '제주 감귤',
    description: 'Bright white with tangerine orange accents',
    descriptionKo: '쨍한 화이트와 싱그러운 귤색 포인트의 제주 테마',
    previewLight: {
      bg: '#ffffff',
      card: '#ffffff',
      accent: '#ff9500',
      text: '#1f1f1f',
      secondary: '#229a54',
    },
    previewDark: {
      bg: '#0f1419',
      card: '#1a2028',
      accent: '#ff9f1c',
      text: '#f5f5f5',
      secondary: '#5cd685',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['bright', 'orange', 'jeju', 'tangerine'],
  },
  // 4. 브루탈
  {
    id: 'brutal',
    name: 'Brutal',
    nameKo: '브루탈',
    description: 'Bold neubrutalism with sharp edges',
    descriptionKo: '두꺼운 테두리와 강렬한 옐로우 포인트의 네오브루탈리즘',
    previewLight: {
      bg: '#fffef9',
      card: '#ffffff',
      accent: '#ffe234',
      text: '#1a1a1a',
      secondary: '#1a1a1a',
    },
    previewDark: {
      bg: '#0a0a0a',
      card: '#1a1a1a',
      accent: '#ffe234',
      text: '#f5f5f5',
      secondary: '#f5f5f5',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['bold', 'brutalism', 'sharp'],
  },
  // 5. 네온
  {
    id: 'neon',
    name: 'Neon',
    nameKo: '네온',
    description: 'Cyberpunk aesthetic with neon glows',
    descriptionKo: '네온 그린과 글로우 이펙트의 사이버펑크 스타일',
    previewLight: {
      bg: '#0d0d1a',
      card: '#151528',
      accent: '#00ff88',
      text: '#e8e8ff',
      secondary: '#ff0088',
    },
    previewDark: {
      bg: '#08080f',
      card: '#0d0d1a',
      accent: '#00ff99',
      text: '#e8e8ff',
      secondary: '#ff0088',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['cyberpunk', 'glow', 'dark'],
  },
  // 6. 파스텔
  {
    id: 'pastel',
    name: 'Pastel',
    nameKo: '파스텔',
    description: 'Soft, dreamy pastel colors',
    descriptionKo: '따뜻한 크림톤과 라벤더 포인트의 몽환적 스타일',
    previewLight: {
      bg: '#faf8f5',
      card: '#ffffff',
      accent: '#a599c7',
      text: '#3d3d3d',
      secondary: '#78a88c',
    },
    previewDark: {
      bg: '#1a1820',
      card: '#242230',
      accent: '#b8aad4',
      text: '#e8e5f0',
      secondary: '#98c8ac',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['soft', 'dreamy', 'calm'],
  },
  // 7. 딥그린
  {
    id: 'deepgreen',
    name: 'Deep Green',
    nameKo: '딥그린',
    description: 'Rich emerald green, luxurious and sophisticated',
    descriptionKo: '찐하고 깊은 에메랄드, 고급스럽고 세련된 느낌',
    previewLight: {
      bg: '#f8faf9',
      card: '#ffffff',
      accent: '#006242',
      text: '#0f1f1a',
      secondary: '#2080b0',
    },
    previewDark: {
      bg: '#040806',
      card: '#0a0e0c',
      accent: '#0c6e4e',
      text: '#e8f0ec',
      secondary: '#50b0e0',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['emerald', 'green', 'luxury', 'deep'],
  },
  // 8. 포레스트
  {
    id: 'forest',
    name: 'Forest',
    nameKo: '포레스트',
    description: 'Natural forest greens with warm wood tones',
    descriptionKo: '숲속의 평화로움, 따뜻한 우드톤과 자연 그린',
    previewLight: {
      bg: '#faf9f6',
      card: '#ffffff',
      accent: '#6b8e50',
      text: '#2d2a26',
      secondary: '#cc8c32',
    },
    previewDark: {
      bg: '#141a12',
      card: '#1c231a',
      accent: '#8ab465',
      text: '#e8e4dc',
      secondary: '#e8b060',
    },
    author: 'Note\'n Team',
    isBuiltIn: true,
    tags: ['nature', 'green', 'forest', 'wood'],
  },
];

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get theme by ID
 */
export function getThemeById(id: DesignStyle): ThemeDefinition | undefined {
  return THEMES.find((theme) => theme.id === id);
}

/**
 * Get preview colors for a theme based on current mode
 */
export function getThemePreview(
  id: DesignStyle,
  isDark: boolean
): ThemePreviewColors | undefined {
  const theme = getThemeById(id);
  if (!theme) return undefined;
  return isDark ? theme.previewDark : theme.previewLight;
}

/**
 * Get CSS class name for a theme
 */
export function getThemeClassName(id: DesignStyle): string {
  // 'soft' is default, no class needed
  if (id === 'soft') return '';
  // All other themes use theme-{id} format
  return `theme-${id}`;
}

export default THEMES;
